/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Select, Loading} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {buildingblocktypeGetId, buildingblocktypeUpdate} from '../../../redux/slices/project.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  loading: state.lookup.loading
})

const selectProjectData = (state) => ({
  isRedirect: state.project.isRedirect,
  detail: state.project.detail,
  loading: state.project.loading
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  const formikRef = useRef(null)
  const isFormPopulatedRef = useRef(false)
  
  const pageUrl = useMemoizedPageUrl(location)

  const {projectLookupList, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectProjectData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(buildingblocktypeGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    ProjectId: '',
    Description: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(buildingblocktypeUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && formikRef.current && !isFormPopulatedRef.current) {
      Object.keys(formValues).forEach(key => {
        const detailKey = key.charAt(0).toLowerCase() + key.slice(1)
        if (detailKey in detail) {
          formikRef.current.setFieldValue(key, detail[detailKey])
        }
      })
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'MENU.PROJECTS.BUILDINGBLOCKTYPE'}),
      path: '/projects/building-block-type',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/projects/building-block-type/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${id}`)
    }
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.BUILDINGBLOCKTYPE.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'BUILDINGBLOCKTYPE.SEARCH.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PROJECT_NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PROJECT.DESCRIPTION'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <textarea
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    rows={2}
                    {...formik.getFieldProps('Description')}
                  />
                </div>
              </div>
              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>{' '}
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
